
import React, { useState, useRef, useEffect } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, Crown, User, Loader2, UserPlus } from 'lucide-react';
import { useContributors, Contributor } from '@/hooks/use-contributors';
import { cn } from '@/lib/utils';

interface ContributorInputProps {
  contributors: Contributor[];
  onContributorsChange: (contributors: Contributor[]) => void;
  currentUserId?: string;
  currentUsername?: string;
  currentUserAvatar?: string | null;
}

const ContributorInput: React.FC<ContributorInputProps> = ({
  contributors,
  onContributorsChange,
  currentUserId,
  currentUsername,
  currentUserAvatar
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  const { searchResults, isSearching, searchUsers } = useContributors();

  // Add current user as creator if not already in contributors
  useEffect(() => {
    if (currentUserId && currentUsername && contributors.length === 0) {
      const creator: Contributor = {
        id: currentUserId,
        username: currentUsername,
        avatar_url: currentUserAvatar || null,
        role: 'creator'
      };
      onContributorsChange([creator]);
    }
  }, [currentUserId, currentUsername, currentUserAvatar, contributors.length, onContributorsChange]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setSelectedIndex(-1);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.startsWith('@') && value.length > 1) {
      const searchTerm = value.slice(1); // Remove @ symbol
      setShowDropdown(true);
      
      // Debounce search
      debounceRef.current = setTimeout(() => {
        searchUsers(searchTerm);
      }, 300);
    } else {
      setShowDropdown(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      if (showDropdown && selectedIndex >= 0 && selectedIndex < filteredResults.length) {
        // Select from dropdown
        selectContributor(filteredResults[selectedIndex]);
      } else if (inputValue.trim()) {
        // Add as plain text contributor
        addPlainTextContributor();
      }
      return;
    }

    if (!showDropdown || filteredResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredResults.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const selectContributor = (contributor: Contributor) => {
    // Check if contributor is already added
    const isAlreadyAdded = contributors.some(c => c.id === contributor.id);
    if (isAlreadyAdded) return;

    const newContributor: Contributor = {
      ...contributor,
      role: 'contributor'
    };

    onContributorsChange([...contributors, newContributor]);
    setInputValue('');
    setShowDropdown(false);
    setSelectedIndex(-1);
  };

  const addPlainTextContributor = () => {
    const name = inputValue.trim();
    if (!name) return;

    // Check if already added
    const isAlreadyAdded = contributors.some(c => 
      c.username === name || (c.id === `plain-${name}`)
    );
    if (isAlreadyAdded) return;

    const plainTextContributor: Contributor = {
      id: `plain-${name}`, // Unique ID for plain text contributors
      username: name,
      avatar_url: null,
      role: 'contributor'
    };

    onContributorsChange([...contributors, plainTextContributor]);
    setInputValue('');
  };

  const removeContributor = (contributorId: string) => {
    // Don't allow removing the creator
    const contributorToRemove = contributors.find(c => c.id === contributorId);
    if (contributorToRemove?.role === 'creator') return;

    onContributorsChange(contributors.filter(c => c.id !== contributorId));
  };

  const filteredResults = searchResults.filter(
    result => !contributors.some(c => c.id === result.id)
  );

  const isPlainTextContributor = (contributor: Contributor) => {
    return contributor.id.startsWith('plain-');
  };

  return (
    <div className="space-y-3">
      <Label htmlFor="contributors">Contributors</Label>
      
      <div className="relative">
        <Input
          ref={inputRef}
          id="contributors"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type @username for users or plain text for external contributors..."
          className="pr-10"
        />
        
        {isSearching && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}

        {showDropdown && filteredResults.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-y-auto"
          >
            {filteredResults.map((user, index) => (
              <div
                key={user.id}
                className={cn(
                  "flex items-center gap-3 p-3 cursor-pointer hover:bg-accent transition-colors",
                  selectedIndex === index && "bg-accent"
                )}
                onClick={() => selectContributor(user)}
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user.avatar_url || undefined} />
                  <AvatarFallback>
                    <User className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium">@{user.username}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Suggestion for plain text */}
      {inputValue && !inputValue.startsWith('@') && inputValue.trim().length > 0 && (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <UserPlus className="h-3 w-3" />
          Press Enter to add "{inputValue.trim()}" as a contributor
        </div>
      )}

      {contributors.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">Selected Contributors:</div>
          <div className="flex flex-wrap gap-2">
            {contributors.map((contributor) => (
              <Badge
                key={contributor.id}
                variant={contributor.role === 'creator' ? 'default' : 'secondary'}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 text-sm",
                  isPlainTextContributor(contributor) && "border-dashed"
                )}
              >
                {!isPlainTextContributor(contributor) ? (
                  <Avatar className="h-5 w-5">
                    <AvatarImage src={contributor.avatar_url || undefined} />
                    <AvatarFallback className="text-xs">
                      <User className="h-3 w-3" />
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  <UserPlus className="h-3 w-3" />
                )}
                
                <span>
                  {isPlainTextContributor(contributor) 
                    ? contributor.username 
                    : `@${contributor.username}`
                  }
                </span>
                
                {contributor.role === 'creator' && (
                  <Crown className="h-3 w-3 text-yellow-500" />
                )}
                
                {contributor.role !== 'creator' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => removeContributor(contributor.id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContributorInput;
